import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import {
  ACTION,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  ExpandAllButton,
  ExportMenuItems,
  EXTRAS_MENU_TOOLTIP_PROPS,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Toggle,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosure,
  useMixpanel,
} from '@talos/kyoko';
import { useMemo } from 'react';
import type { ClonePositionsTabFunc, SubAccountPositionsTabProps } from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { RecalculateSubAccountHistoryDialog } from './RecalculateSubAccountHistoryDialog';
import { SubAccountTreeWarning } from './SubAccountTreeWarning';
import { treeBlotterContextMenuColumnResolver } from './treeBlotterContextMenuColumnResolver';
import { usePositionsSubAccountsBlotterFoundation } from './usePositionsSubAccountsBlotterFoundation';
import { usePositionsSubAccountsWsBlotter } from './usePositionsSubAccountsWsBlotter';

export interface PositionsSubAccountsBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: SubAccountPositionsTabProps;
  onUpdateTab: (tab: SubAccountPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

// Small wrapper around the meat of the component to remount it when the hierarchical setting changes.
export function PositionsSubAccountsBlotter(props: PositionsSubAccountsBlotterParams) {
  return <PositionsSubAccountsBlotterInner key={`${!!props.tab.hierarchical}`} {...props} />;
}

function PositionsSubAccountsBlotterInner({
  blotterID,
  initialIsOpen,
  tab,
  onUpdateTab,
  onCloneTab,
}: PositionsSubAccountsBlotterParams) {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { enableSubAccountHistoricalRecalc } = useFeatureFlag();
  const isAuthorizedToEditPositions = isAuthorized(ACTION.EDIT_POSITION);
  const recalculateHistoryDialog = useDisclosure();

  const foundation = usePositionsSubAccountsBlotterFoundation({ blotterID, tab });
  const {
    columns,
    filterResults,
    hierarchical,
    showZeroBalances,
    includeCash,
    enableHierarchicalSubAccountPositionsBlotter,
  } = foundation;

  const { filterBuilderProps } = filterResults;

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const updateIncludeCash = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleIncludeCash, {
      [MixpanelEventProperty.Enabled]: value,
      [MixpanelEventProperty.Source]: MixpanelEventSource.SubAccountPositionsBlotter,
    });
    onUpdateTab({ ...tab, includeCash: value });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Sub Accounts',
    getColIdForFiltering: hierarchical ? treeBlotterContextMenuColumnResolver : undefined,
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(() => [...columns, ...positionsMenu.columns], [columns, positionsMenu.columns]);

  const { blotterTable, groupingToggleButtons, expandAllGroups, collapseAllGroups } = usePositionsSubAccountsWsBlotter({
    blotterID,
    tab,
    foundation,
    columns: columnsWithMenu,
    getExtraMainMenuItems: positionsMenu.getExtraMainMenuItems,
    contextMenuOptions: positionsMenu.contextMenuOptions,
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const updateHierarchicalSetting = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleSubAccountPositionsBlotterHierarchical, {
      [MixpanelEventProperty.Checked]: value,
    });
    onUpdateTab({ ...tab, hierarchical: value });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        accordionBodyPrefix={!hierarchical ? groupingToggleButtons : null}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <>
            {/* If our hierarchy is proven to not be a tree, and we are showing the hierarchical blotter, then warn the user. */}
            {hierarchical && <SubAccountTreeWarning />}
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Toggle
                tooltip={CONTROL_TOOLTIPS.includeCash}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={includeCash}
                onChange={updateIncludeCash}
                label="Include Cash"
              />
              {enableHierarchicalSubAccountPositionsBlotter && (
                <Toggle
                  tooltip={CONTROL_TOOLTIPS.showRollupHierarchy}
                  tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                  size={FormControlSizes.Small}
                  checked={hierarchical}
                  onChange={updateHierarchicalSetting}
                  label="Show Rollup Hierarchy"
                />
              )}
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
                data-testid="clone-tab-button"
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

              {isAuthorizedToEditPositions && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={() => positionsMenu.openUpdatePositionDialog()}
                >
                  Adjust Sub Account Position
                </Button>
              )}
              {isAuthorizedToEditPositions && enableSubAccountHistoricalRecalc && (
                <Button
                  startIcon={IconName.Refresh}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={() => recalculateHistoryDialog.open()}
                  data-testid="recalculate-sub-account-history-button"
                >
                  Recalculate History
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
      <RecalculateSubAccountHistoryDialog dialogProps={recalculateHistoryDialog} />
    </>
  );
}
